import { DateTimeValueSchemas } from "@validereinc/utilities";
import { z } from "zod";
import {
  createExportListRequestSchema,
  createGetListRequestSchema,
  DomainModelSchema,
  IdentifierSchema,
  Resources,
  type ResourceType,
} from "../util/index.ts";
import { AssetType } from "./AssetSchemas.ts";
import { type DeviceType } from "./DeviceSchemas.ts";
import { type EquipmentType } from "./EquipmentSchemas.ts";
import { type FacilityType } from "./FacilitySchemas.ts";

export const ActivityAction = {
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
  ARCHIVE: "archive",
  PERMISSION_UPDATE: "permission_update",
  USER_ADD: "user_add",
  USER_REMOVE: "user_remove",
} as const;

export const ActivityActionSchema = z.union([
  z.literal(ActivityAction.CREATE),
  z.literal(ActivityAction.UPDATE),
  z.literal(ActivityAction.DELETE),
  z.literal(ActivityAction.ARCHIVE),
  z.literal(ActivityAction.PERMISSION_UPDATE),
  z.literal(ActivityAction.USER_ADD),
  z.literal(ActivityAction.USER_REMOVE),
]);
export type ActivityActionType = z.infer<typeof ActivityActionSchema>;

export const ActivityResource = {
  FACILITY: Resources.FACILITY,
  EQUIPMENT: Resources.EQUIPMENT,
  FLOW: Resources.FLOW,
  ASSET_GROUP: Resources.ASSET_GROUP,
  DEVICE: Resources.DEVICE,
  RECORD_VALUE: Resources.RECORD_VALUE,
  ROLE: Resources.ROLE,
  USER_GROUP: Resources.USER_GROUP,
} as const;
export type ActivityResourceType = z.infer<typeof ActivityResourceSchema>;

// IMPROVE: there will be a better place for this once we figure out resource name standardization
export const ActivityResourceSchema = z.union([
  z.literal(ActivityResource.RECORD_VALUE),
  z.literal(ActivityResource.FLOW),
  z.literal(ActivityResource.FACILITY),
  z.literal(ActivityResource.EQUIPMENT),
  z.literal(ActivityResource.DEVICE),
  z.literal(ActivityResource.ASSET_GROUP),
  z.literal(ActivityResource.ROLE),
  z.literal(ActivityResource.USER_GROUP),
]);

/**
 * Note, this schema is only used to reference keys safely
 */
export const ActivitySchema = z
  .object({
    company_id: IdentifierSchema,
    resource_id: IdentifierSchema,
    // IMPROVE: this will likely be it's own schema once we figure out resource name standardization
    resource_type: ActivityResourceSchema,
    action: ActivityActionSchema,
    // can be anything depending on the resource the log was for
    before: z.any().optional(),
    // can be anything depending on the resource the log was for
    after: z.any().optional(),
    author_id: IdentifierSchema,
    timestamp: z.string().datetime(),
  })
  .merge(DomainModelSchema)
  .describe("Describes an Activity model in the Node API");
/**
 * Describes an Activity model for a specific resource in the Node API. Note,
 * generic ActivityType is not derived from the ActivitySchema on purpose as the
 * type is primarily used and needs generic flexibility
 */
export type ActivityType<
  BeforeAfter = unknown,
  Resource extends string = ResourceType,
> = {
  id: string;
  company_id: string;
  resource_id: string;
  // IMPROVE: this will likely be it's own schema once we figure out resource name standardization
  resource_type: Resource;
  action: ActivityActionType;
  // can be anything depending on the resource the log was for
  before?: BeforeAfter;
  // can be anything depending on the resource the log was for
  after?: BeforeAfter;
  author_id: string;
  timestamp: string;
};
export type AnyActivityType = ActivityType;
export type FacilityActivityType = ActivityType<
  FacilityType,
  typeof AssetType.FACILITY
>;
export type EquipmentActivityType = ActivityType<
  EquipmentType,
  typeof AssetType.EQUIPMENT
>;
export type DeviceActivityType = ActivityType<
  DeviceType,
  typeof AssetType.DEVICE
>;
export type ResourceActivityType =
  | FacilityActivityType
  | EquipmentActivityType
  | DeviceActivityType;

export const ActivityExportResource = {
  ROLE: Resources.ROLE,
  USER_GROUP: Resources.USER_GROUP,
  USER: Resources.USER,
} as const;
export type ActivityExportResourceType =
  (typeof ActivityExportResource)[keyof typeof ActivityExportResource];

export const ActivityGetListRequestSchema = createGetListRequestSchema(
  ActivitySchema.pick({
    id: true,
    resource_id: true,
    author_id: true,
    action: true,
    timestamp: true,
  }),
  z.object({
    resourceType: z.nativeEnum(ActivityResource),
  })
);
export type ActivityGetListRequestType = z.infer<
  typeof ActivityGetListRequestSchema
>;

export const ActivityExportListRequestSchema = createExportListRequestSchema(
  ActivityGetListRequestSchema.shape.filters.unwrap(),
  z.object({
    resourceType: z.nativeEnum(ActivityExportResource),
    period: DateTimeValueSchemas.dateYearMonth,
  })
);
export type ActivityExportListRequestType = z.infer<
  typeof ActivityExportListRequestSchema
>;

export type AssetActivityLogAssetType = Extract<
  ActivityResourceType,
  "FACILITY" | "FLOW" | "EQUIPMENT" | "DEVICE"
>;
